import { LabelAssignment } from '@provision/entity-types';
import { ApolloCache, gql, useMutation, useQuery } from '@provision/graphql-client';
import { useCallback, useEffect, useMemo } from 'react';
import { wsEventBus } from 'src/core/realtime-connection';
import { QueryVariables, Records } from 'src/graphql/types';
import { useNotifications } from 'src/utils/hooks';

const GET_CTA_LABEL = gql`
  query GetCtaLabels($input: GetCtaLabelInput!) {
    getCtaLabels(input: $input) {
      records {
        clientId
        addressId
        labelId
        partnerId
        labelName
        labelIsPublic
      }
    }
  }
`;

interface LabelToCta {
  labelAssignmentId?: string;
  labelId: string;
  clientId: string;
  addressId: string;
  partnerId: number;
}

export interface CtaLabel {
  clientId: string;
  addressId: string;
  labelId: string;
  partnerId: number;
  labelName: string;
  labelIsPublic: boolean;
  labelGroupId?: string;
}

interface GetCtaLabels {
  getCtaLabels: Records<CtaLabel>;
}

export const useCtaLabels = (rawClientIds: string[] | string, addressId?: string) => {
  const { queryOptions, clientIds } = useMemo(() => {
    const clientIds = Array.isArray(rawClientIds) ? rawClientIds : [rawClientIds];

    return {
      clientIds,
      queryOptions: {
        variables: {
          input: {
            clientIds,
          },
        },
        skip: !clientIds?.length,
      },
    };
  }, [rawClientIds]);

  const {
    data: rawData,
    previousData,
    client,
    ...rest
  } = useQuery<GetCtaLabels>(GET_CTA_LABEL, queryOptions);

  useEffect(() => {
    const clientToAddressSubscription = wsEventBus
      .onEntity<LabelAssignment>({ name: 'LabelAssignment', operation: 'update' })
      .subscribe({
        next: (message) => {
          updateCachedClientLabelsQuery({
            cache: client.cache,
            clientIds,
            addLabels: [message.attributes],
          });
        },
      });

    const deleteClientToAddressSubscription = wsEventBus
      .onEntity<LabelAssignment>({ name: 'LabelAssignment', operation: 'delete' })
      .subscribe({
        next: (message) =>
          updateCachedClientLabelsQuery({
            cache: client.cache,
            clientIds,
            removeLabels: [message.previousAttributes],
          }),
      });

    return () => {
      clientToAddressSubscription.unsubscribe();
      deleteClientToAddressSubscription.unsubscribe();
    };
  }, [clientIds, client]);

  const ctaLabels = useMemo(() => {
    const data = rawData || previousData;

    let ctaLabels = data?.getCtaLabels.records;

    if (addressId) {
      ctaLabels = ctaLabels?.filter((label) => label.addressId === addressId);
    }

    return ctaLabels;
  }, [rawData, previousData, addressId]);

  return { ctaLabels, ...rest };
};

export const ASSIGN_LABEL_TO_CLIENT_TO_ADDRESS = gql`
  mutation AssignLabelToClientToAddress($data: AssignLabelToClientToAddressInput) {
    assignLabelToClientToAddress(data: $data) {
      labelId
      clientId
      addressId
      partnerId
    }
  }
`;

export const DELETE_LABEL_TO_CLIENT_TO_ADDRESS = gql`
  mutation DeleteLabelToClientToAddress($condition: DeleteLabelToClientToAddressInput) {
    deleteLabelToClientToAddress(condition: $condition) {
      labelAssignmentId
      labelId
      partnerId
      entity
      entityId
    }
  }
`;

const GET_CLIENT_PHONE_LABELS = gql`
  query GetClientPhoneLabels($params: QueryParams) @entity(name: "ClientPhoneLabel") {
    getClientPhoneLabels(queryParams: $params) {
      records {
        id
        label
      }
    }
  }
`;

export const useLabelToCta = () => {
  const { showErrorAlert } = useNotifications();
  const [deleteLabelToCtaMutation] = useMutation<{
    deleteLabelToClientToAddress: { labelId: string }[];
  }>(DELETE_LABEL_TO_CLIENT_TO_ADDRESS, {
    onError: (error) => {
      console.error(error);
      showErrorAlert('Something went wrong deleting the label, please try again');
    },
  });

  const deleteLabelToCta = useCallback(
    async (label: { labelId?: string; clientId?: string; addressId?: string }) => {
      const { data } = await deleteLabelToCtaMutation({
        variables: { condition: label },
        optimisticResponse: {
          deleteLabelToClientToAddress: [{ labelId: label.labelId! }],
        },
        update(cache, { data }) {
          if (!data?.deleteLabelToClientToAddress?.[0]?.labelId) return;

          updateCachedClientLabelsQuery({
            cache,
            clientIds: [label.clientId!],
            removeLabels: [
              {
                ...label,
                ...data.deleteLabelToClientToAddress[0],
              },
            ],
          });
        },
      });

      return data?.deleteLabelToClientToAddress;
    },
    [deleteLabelToCtaMutation],
  );

  const [assignLabelToCtaMutation] = useMutation<{
    assignLabelToClientToAddress: LabelToCta & { id: string };
  }>(ASSIGN_LABEL_TO_CLIENT_TO_ADDRESS, {
    onError: (error) => {
      console.error(error);
      showErrorAlert('Something went wrong creating a new label, please try again');
    },
  });

  const assignLabelToCta = useCallback(
    async (label: LabelToCta) => {
      const createdAt = new Date().toISOString();
      const tempId = `temp-id${createdAt}`;

      const { data } = await assignLabelToCtaMutation({
        variables: {
          data: {
            partnerId: label.partnerId,
            labelId: label.labelId,
            clientId: label.clientId,
            addressId: label.addressId,
          },
        },
        optimisticResponse: {
          assignLabelToClientToAddress: {
            id: label.labelAssignmentId || `${new Date().getTime()}`,
            labelAssignmentId: label.labelAssignmentId!,
            labelId: label.labelId,
            clientId: label.clientId ?? tempId,
            addressId: label.addressId ?? tempId,
            partnerId: label.partnerId,
          },
        },
        update(cache, { data }) {
          if (!data?.assignLabelToClientToAddress?.labelId) return;

          updateCachedClientLabelsQuery({
            cache,
            clientIds: [label.clientId],
            addLabels: [
              {
                ...label,
                ...data.assignLabelToClientToAddress,
              },
            ],
          });
        },
      });

      return data?.assignLabelToClientToAddress;
    },
    [assignLabelToCtaMutation],
  );

  return {
    assignLabelToCta,
    deleteLabelToCta,
  };
};

function updateCachedClientLabelsQuery({
  cache,
  clientIds,
  addLabels,
  removeLabels,
}: {
  cache: ApolloCache<unknown>;
  clientIds: string[];
  addLabels?: Partial<LabelToCta>[];
  removeLabels?: Partial<LabelToCta>[];
}) {
  if (!clientIds?.length) return;

  cache.updateQuery<GetCtaLabels>(
    {
      query: GET_CTA_LABEL,
      variables: {
        input: {
          clientIds,
        },
      },
    },
    (existingData) => {
      let records = [
        ...existingData.getCtaLabels.records,
        ...((addLabels ?? []) as CtaLabel[]),
      ];

      if (removeLabels?.length) {
        records = records.filter(
          (record) =>
            !removeLabels.some(
              (removeLabel) =>
                record.labelId === removeLabel.labelId &&
                record.clientId === removeLabel.clientId,
            ),
        );
      }

      return { getCtaLabels: { records } };
    },
  );
}

const GET_CLIENT_EMAIL_LABELS = gql`
  query GetClientEmailLabels($params: QueryParams) @entity(name: "ClientEmailLabel") {
    getClientEmailLabels(queryParams: $params) {
      records {
        id
        label
      }
    }
  }
`;

export const usePhonesAndEmailsLabels = () => {
  const { data: emailData, loading: emailLoading } = useQuery<
    {
      getClientEmailLabels: Records<{
        id: number;
        label: string;
      }>;
    },
    QueryVariables
  >(GET_CLIENT_EMAIL_LABELS);

  const clientEmailLabels = emailData?.getClientEmailLabels.records;

  const { data: phoneData, loading: phoneLoading } = useQuery<
    {
      getClientPhoneLabels: Records<{
        id: number;
        label: string;
      }>;
    },
    QueryVariables
  >(GET_CLIENT_PHONE_LABELS);

  const clientPhoneLabels = phoneData?.getClientPhoneLabels.records;

  return {
    clientEmailLabels,
    clientPhoneLabels,
    loading: emailLoading || phoneLoading,
  };
};
